import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Typography, IconButton, Spinner, Button } from '@material-tailwind/react'
import { colors } from '@material-tailwind/react/types/generic'
import { PiSpeakerX, PiTrash } from 'react-icons/pi'
import truncate from 'lodash/truncate'
import NoUploads from '../components/NoUploads'
import PlayerMini from '../components/PlayerMini'
import AgamiTypeChip from '../components/AgamiTypeChip'
import AgamiMediaTypeChip from '../components/AgamiMediaTypeChip'
import TableHeaderWithSorting from '../components/TableHeaderWithSorting'
import { getAgamiMediaState, removeManualCodeLibrarySuffix } from '../utils/helpers/agami.helpers'
import { formatDateString } from '../utils/helpers/dates.helpers'
import AgamiDraftChip from '../components/AgamiDraftChip'
import { ProposalActions, ProposalContext } from '../components/ProposalModeration'
import { AgamiSpot } from '../types/supabase'
import { useAgamiRoutes } from '../hooks/useAgamiRoutes'

interface ExperienceSpotsListProps {
  agamiListLoading: boolean
  agamiList: AgamiSpot[]
  agamiCount?: number
  removeFromExperience?: (spot: AgamiSpot) => void
  /** Whether the caller holds `moderate` (05.4): without it the actions are ABSENT, not disabled. */
  canModerate?: boolean
  onRejectProposal?: (spot: AgamiSpot) => void
  showOwner?: boolean
  pageSize?: number
  page?: number
  setPage?: (page: number) => void
  sort?: string
  setSort?: (sort: string) => void
}

export const ExperienceSpotsList: React.FC<ExperienceSpotsListProps> = ({
  agamiListLoading,
  agamiList,
  agamiCount,
  removeFromExperience,
  canModerate,
  onRejectProposal,
  showOwner,
  pageSize,
  page,
  setPage,
  sort,
  setSort,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { agamiFormPath } = useAgamiRoutes()

  const remove = async (e: React.MouseEvent<HTMLButtonElement>, spot: AgamiSpot) => {
    e.preventDefault()
    e.stopPropagation()
    removeFromExperience?.(spot)
  }

  const TABLE_HEAD = [
    { title: 'Name', sortKey: 'name' },
    { title: 'Description' },
    { title: 'Last update', sortKey: 'updated_at' },
    { title: 'Media' },
    { title: 'Audio' },
    ...(showOwner ? [{ title: 'Owner', sortKey: 'profiles.name' }] : []),
    { title: 'Type', sortKey: 'type' },
    ...(removeFromExperience ? [{ title: '' }] : []),
  ]

  const lastPage = useMemo(
    () => (agamiCount && pageSize ? Math.trunc(agamiCount / pageSize) + 1 : undefined),
    [agamiCount, pageSize]
  )

  const pageSelector = useMemo(
    () =>
      agamiCount && pageSize && page && lastPage
        ? Array.from(
            { length: Math.max(Math.min(3, lastPage - 2), 0) },
            (_, i) => i + Math.max(Math.min(page - 1, lastPage - 3), 2)
          )
        : undefined,
    [agamiCount, pageSize, page, lastPage]
  )

  return (
    <div className="py-2">
      {agamiListLoading && <Spinner className="h-12 w-12 m-auto" />}

      {!agamiListLoading && (!agamiList || agamiList.length === 0) && (
        <div className="col-span-2" data-testid="experience-spots-empty">
          <NoUploads>
            <Typography>
              No <em>agamis</em> here yet.
            </Typography>
          </NoUploads>
        </div>
      )}

      {!agamiListLoading && agamiList?.length > 0 && (
        <table className="w-full">
          <thead>
            <tr>
              {TABLE_HEAD.map((head) => (
                <TableHeaderWithSorting
                  key={head.title}
                  title={head.title}
                  sortKey={head.sortKey}
                  sort={sort}
                  setSort={setSort}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {agamiList?.map((agami) => (
              <tr
                key={agami.id}
                className="even:bg-blue-gray-50/50 dark:even:bg-primary-500 hover:bg-white-yellow dark:hover:bg-primary-300 cursor-pointer"
                data-testclass="experience-spot-row"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  navigate(agamiFormPath(agami.id))
                }}
              >
                <td className="px-4">
                  <Typography title={agami.name} variant="small" className="font-black">
                    {truncate(agami.name, {
                      separator: ' ',
                    })}
                  </Typography>
                  {/* The same badge the cards carry (05.3/05.6): before this, a
                      draft in the table view was indistinguishable from a
                      published word. One shared component, so the two views
                      cannot diverge on what a draft (or a proposal) looks like. */}
                  {agami.is_draft && (
                    <div className="mb-1">
                      <AgamiDraftChip spot={agami} />
                    </div>
                  )}
                  {/* Missing declared media is an error next to the name. An
                      intentional no-media declaration already has its dedicated
                      Media column and must not be repeated here. */}
                  {getAgamiMediaState(agami) === 'awaiting-upload' && (
                    <span
                      title={t('agami.missingAudio')}
                      className="inline-flex align-middle ml-1 text-orange-700 dark:text-orange-300"
                      data-testclass="no-media-indicator"
                    >
                      <PiSpeakerX className="w-4 h-4" />
                    </span>
                  )}
                  {agami.proposal && <ProposalContext proposal={agami.proposal} />}
                </td>
                <td className="px-4">
                  <Typography title={agami.description} variant="small">
                    {truncate(agami.description, {
                      separator: ' ',
                    })}
                  </Typography>
                </td>
                <td className="px-4">
                  <Typography variant="small">
                    {agami?.updated_at ? formatDateString(agami.updated_at) : ''}
                  </Typography>
                </td>
                <td className="px-4">
                  <AgamiMediaTypeChip
                    mediaType={agami?.content_media_type}
                    dataTestClass="experience-media-type"
                  />
                </td>
                <td className="px-4">
                  {agami?.content_media_type === 1 && agami.audioUrl && (
                    <PlayerMini audioUrl={agami.audioUrl} />
                  )}
                </td>
                {showOwner && (
                  <td className="px-4">
                    <Typography
                      title={`${agami.profiles?.name} ${agami.profiles?.surname}`}
                      variant="small"
                      className="font-black"
                    >
                      {truncate(`${agami.profiles?.name} ${agami.profiles?.surname}`, {
                        separator: ' ',
                      })}
                    </Typography>
                  </td>
                )}
                <td className="px-4 py-2 flex gap-x-2 items-center ">
                  <AgamiTypeChip agamiType={agami.type} />
                  {agami.type === 2 && agami?.experience_content_spots?.[0]?.manual_code && (
                    <Typography variant="small" className="font-bold">
                      {removeManualCodeLibrarySuffix(agami.experience_content_spots[0].manual_code)}
                    </Typography>
                  )}
                </td>
                {removeFromExperience && (
                  <td className="px-4 text-right">
                    {/* A proposal is not "removed from the experience": rejecting
                        it is the one path out (05.4), so its cell swaps the trash
                        for the moderation actions — which exist only with
                        `moderate` (AC-6: absent, not disabled). */}
                    {agami.proposal ? (
                      canModerate &&
                      onRejectProposal && <ProposalActions spot={agami} onReject={onRejectProposal} />
                    ) : (
                      <IconButton
                        variant="text"
                        size="sm"
                        color="yellow"
                        onClick={(e) => remove(e, agami)}
                        data-testid="experience-spot-remove-btn"
                      >
                        <PiTrash className="text-primary dark:text-base-gray-500 text-[20px]" />
                      </IconButton>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!agamiListLoading && page && setPage && pageSize && pageSelector && lastPage && agamiCount && (
        <div className="flex items-center justify-between gap-2 mt-4">
          <div className="flex gap-4">
            <Typography variant="small">
              {`${(page - 1) * pageSize + 1} - ${Math.min(page * pageSize, agamiCount)}`}
              {' of '}
              <span className="font-bold">{agamiCount}</span>
            </Typography>
          </div>
          <div className="flex gap-2">
            <Button
              color={'primary' as colors}
              variant="outlined"
              size="sm"
              disabled={page <= 1}
              onClick={() => {
                if (page > 1) {
                  setPage(page - 1)
                }
              }}
            >
              &lt;&lt;
            </Button>

            <Button
              color={'primary' as colors}
              variant={page === 1 ? 'filled' : 'outlined'}
              size="sm"
              onClick={() => setPage(1)}
            >
              {1}
            </Button>

            {pageSelector && pageSelector.length > 0 && pageSelector[0]! > 2 && (
              <Typography variant="h4" className="font-normal">
                ...
              </Typography>
            )}

            {pageSelector.map((idx) => (
              <Button
                key={idx}
                color={'primary' as colors}
                variant={page === idx ? 'filled' : 'outlined'}
                size="sm"
                onClick={() => setPage(idx)}
              >
                {idx}
              </Button>
            ))}

            {lastPage > 1 &&
              pageSelector &&
              pageSelector.length > 0 &&
              pageSelector[pageSelector.length - 1]! < lastPage - 1 && (
                <Typography variant="h4" className="font-normal">
                  ...
                </Typography>
              )}

            {lastPage > 1 && (
              <Button
                color={'primary' as colors}
                variant={page === lastPage ? 'filled' : 'outlined'}
                size="sm"
                onClick={() => setPage(lastPage)}
              >
                {lastPage}
              </Button>
            )}

            <Button
              color={'primary' as colors}
              variant="outlined"
              size="sm"
              disabled={page >= lastPage}
              onClick={() => {
                if (page < lastPage) {
                  setPage(page + 1)
                }
              }}
            >
              &gt;&gt;
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
